import type {
  AccountOverview,
  AccountStorage,
  AdminAccount,
  AdminIntegrations,
  AdminOverview,
  CheckoutOrder,
  SearchResults,
  UpdatePaymentsSettingsInput,
  VerifyPaymentInput,
  ApiError,
  AuditEvent,
  BoardRow,
  ProjectBoardGroup,
  Comment,
  CommentThread,
  CreateCommentInput,
  CreateIssueInput,
  CreateProjectInput,
  CreateShareLinkInput,
  CreateThreadInput,
  CreateWorkspaceInput,
  ShareLink,
  SharePortal,
  UpdateShareLinkInput,
  InviteInfo,
  InviteMemberInput,
  InviteResult,
  MembershipRole,
  PendingInvite,
  WorkspacePlan,
  Issue,
  LoginInput,
  Project,
  Proof,
  ProofDetail,
  RecycleItem,
  RecycleItemType,
  RegisterInput,
  RegisterResult,
  UpdateEmailSettingsInput,
  UpdateProfileInput,
  SessionUser,
  StorageTestResult,
  SubmitDecisionInput,
  VersionDetail,
  VersionReview,
  Workspace,
  WorkspaceMember,
} from '@katsquare/types';

/** Error thrown for non-2xx API responses, carrying the parsed envelope. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError | undefined,
  ) {
    super(ApiRequestError.messageOf(body) ?? `Request failed (${status})`);
    this.name = 'ApiRequestError';
  }

  private static messageOf(body: ApiError | undefined): string | undefined {
    if (!body) return undefined;
    return Array.isArray(body.message) ? body.message.join('\n') : body.message;
  }
}

type Options = { method?: string; body?: unknown };

/**
 * Fetch relative to `/api` (proxied by Vite in dev, Caddy in prod). Sends and
 * receives cookies. Throws {@link ApiRequestError} on failure.
 */
async function apiFetch<T>(path: string, opts: Options = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body ? { 'content-type': 'application/json', accept: 'application/json' } : { accept: 'application/json' },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    throw new ApiRequestError(res.status, json as ApiError | undefined);
  }
  return json as T;
}


// ── Auth ─────────────────────────────────────────────────────────────────────
export const register = (input: RegisterInput) =>
  apiFetch<RegisterResult>('/auth/register', { method: 'POST', body: input });
export const login = (input: LoginInput) =>
  apiFetch<SessionUser>('/auth/login', { method: 'POST', body: input });
export const logout = () => apiFetch<void>('/auth/logout', { method: 'POST' });
export const verifyEmail = (token: string) =>
  apiFetch<SessionUser>('/auth/verify-email', { method: 'POST', body: { token } });
export const resendVerification = (email: string) =>
  apiFetch<void>('/auth/resend-verification', { method: 'POST', body: { email } });
export const forgotPassword = (email: string) =>
  apiFetch<void>('/auth/forgot-password', { method: 'POST', body: { email } });
export const resetPassword = (token: string, password: string) =>
  apiFetch<SessionUser>('/auth/reset-password', { method: 'POST', body: { token, password } });
export const updateProfile = (input: UpdateProfileInput) =>
  apiFetch<SessionUser>('/auth/me', { method: 'PATCH', body: input });
/** Mark the first-run welcome intro as seen/skipped. */
export const markOnboarded = () =>
  apiFetch<SessionUser>('/auth/me/onboarded', { method: 'POST' });
/** Whether public sign-ups are currently open (for the register page). */
export const getRegistrationStatus = () =>
  apiFetch<{ open: boolean }>('/auth/registration-status');

// ── Account (storage meter + global search) ──────────────────────────────────
export const getAccountStorage = () => apiFetch<AccountStorage>('/me/storage');
export const getAccountOverview = () => apiFetch<AccountOverview>('/me/overview');
export const searchAll = (q: string) =>
  apiFetch<SearchResults>(`/search?q=${encodeURIComponent(q)}`);
/** Downgrade the current account to Free (paid plans go through checkout). */
export const setMyPlan = (plan: WorkspacePlan) =>
  apiFetch<SessionUser>('/auth/me/plan', { method: 'PATCH', body: { plan } });

// ── Billing (Razorpay) ───────────────────────────────────────────────────────
export const startCheckout = (plan: 'PRO' | 'BUSINESS') =>
  apiFetch<CheckoutOrder>('/billing/checkout', { method: 'POST', body: { plan } });
export const verifyPayment = (input: VerifyPaymentInput) =>
  apiFetch<SessionUser>('/billing/verify', { method: 'POST', body: input });
export const updateAdminPayments = (input: UpdatePaymentsSettingsInput) =>
  apiFetch<AdminIntegrations>('/admin/integrations/payments', { method: 'PATCH', body: input });

/** Current user, or null if unauthenticated. */
export async function getMe(): Promise<SessionUser | null> {
  try {
    return await apiFetch<SessionUser>('/auth/me');
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 401) return null;
    throw err;
  }
}

// ── Org ──────────────────────────────────────────────────────────────────────
export const listWorkspaces = () => apiFetch<Workspace[]>('/workspaces');
export const getWorkspace = (workspaceId: string) => apiFetch<Workspace>(`/workspaces/${workspaceId}`);
export const createWorkspace = (input: CreateWorkspaceInput) =>
  apiFetch<Workspace>('/workspaces', { method: 'POST', body: input });
/** Persist the user's drag-reordered sidebar order of workspaces. */
export const reorderWorkspaces = (orderedIds: string[]) =>
  apiFetch<void>('/workspaces/reorder', { method: 'PATCH', body: { orderedIds } });

// ── Members & plan ─────────────────────────────────────────────────────────
export const listWorkspaceMembers = (workspaceId: string) =>
  apiFetch<WorkspaceMember[]>(`/workspaces/${workspaceId}/members`);
export const inviteMember = (workspaceId: string, input: InviteMemberInput) =>
  apiFetch<InviteResult>(`/workspaces/${workspaceId}/members`, { method: 'POST', body: input });
export const listInvites = (workspaceId: string) =>
  apiFetch<PendingInvite[]>(`/workspaces/${workspaceId}/invites`);
export const revokeInvite = (workspaceId: string, inviteId: string) =>
  apiFetch<void>(`/workspaces/${workspaceId}/invites/${inviteId}`, { method: 'DELETE' });
export const getInvite = (token: string) => apiFetch<InviteInfo>(`/invites/${token}`);
export const acceptInvite = (token: string) =>
  apiFetch<Workspace>(`/invites/${token}/accept`, { method: 'POST' });
export const setMemberRole = (workspaceId: string, memberId: string, role: MembershipRole) =>
  apiFetch<void>(`/workspaces/${workspaceId}/members/${memberId}`, { method: 'PATCH', body: { role } });
export const removeMember = (workspaceId: string, memberId: string) =>
  apiFetch<void>(`/workspaces/${workspaceId}/members/${memberId}`, { method: 'DELETE' });
export const renameWorkspace = (workspaceId: string, name: string) =>
  apiFetch<Workspace>(`/workspaces/${workspaceId}`, { method: 'PATCH', body: { name } });
export const deleteWorkspace = (workspaceId: string) =>
  apiFetch<void>(`/workspaces/${workspaceId}`, { method: 'DELETE' });

// ── Recycle bin ────────────────────────────────────────────────────────────
export const listRecycleBin = () => apiFetch<RecycleItem[]>('/recycle-bin');
export const restoreItem = (type: RecycleItemType, id: string) =>
  apiFetch<void>(`/recycle-bin/${type}/${id}/restore`, { method: 'POST' });
export const purgeItem = (type: RecycleItemType, id: string) =>
  apiFetch<void>(`/recycle-bin/${type}/${id}`, { method: 'DELETE' });
export const emptyRecycleBin = () => apiFetch<void>('/recycle-bin', { method: 'DELETE' });

export const listProjects = (workspaceId: string) =>
  apiFetch<Project[]>(`/workspaces/${workspaceId}/projects`);
export const getProject = (projectId: string) => apiFetch<Project>(`/projects/${projectId}`);
export const createProject = (workspaceId: string, input: CreateProjectInput) =>
  apiFetch<Project>(`/workspaces/${workspaceId}/projects`, { method: 'POST', body: input });
export const renameProject = (projectId: string, name: string) =>
  apiFetch<Project>(`/projects/${projectId}`, { method: 'PATCH', body: { name } });
export const deleteProject = (projectId: string) =>
  apiFetch<void>(`/projects/${projectId}`, { method: 'DELETE' });
/** Persist the drag-reordered order of magazines within a workspace. */
export const reorderProjects = (workspaceId: string, orderedIds: string[]) =>
  apiFetch<void>(`/workspaces/${workspaceId}/projects/reorder`, {
    method: 'PATCH',
    body: { orderedIds },
  });

export const listIssues = (projectId: string) =>
  apiFetch<Issue[]>(`/projects/${projectId}/issues`);
export const createIssue = (projectId: string, input: CreateIssueInput) =>
  apiFetch<Issue>(`/projects/${projectId}/issues`, { method: 'POST', body: input });
export const getIssue = (issueId: string) => apiFetch<Issue>(`/issues/${issueId}`);
export const renameIssue = (issueId: string, name: string) =>
  apiFetch<Issue>(`/issues/${issueId}`, { method: 'PATCH', body: { name } });
export const deleteIssue = (issueId: string) =>
  apiFetch<void>(`/issues/${issueId}`, { method: 'DELETE' });

// ── Proofs ─────────────────────────────────────────────────────────────────

/**
 * Upload a file as multipart form-data. Distinct from {@link apiFetch}, which
 * JSON-encodes; here the browser sets the multipart Content-Type + boundary.
 */
async function apiUpload<T>(path: string, file: File, fields?: Record<string, string>): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  for (const [k, v] of Object.entries(fields ?? {})) form.append(k, v);

  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json' },
    body: form,
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) throw new ApiRequestError(res.status, json as ApiError | undefined);
  return json as T;
}

export const listProofs = (issueId: string) =>
  apiFetch<Proof[]>(`/issues/${issueId}/proofs`);
export const getProof = (proofId: string) => apiFetch<ProofDetail>(`/proofs/${proofId}`);
export const renameProof = (proofId: string, name: string) =>
  apiFetch<ProofDetail>(`/proofs/${proofId}`, { method: 'PATCH', body: { name } });
export const moveProof = (proofId: string, issueId: string) =>
  apiFetch<ProofDetail>(`/proofs/${proofId}/move`, { method: 'PATCH', body: { issueId } });
export const deleteProof = (proofId: string) =>
  apiFetch<void>(`/proofs/${proofId}`, { method: 'DELETE' });
/** Direct URL to download a version's original file (use as an <a href>). */
export const versionOriginalUrl = (versionId: string) => `/api/versions/${versionId}/original`;
export const getVersion = (versionId: string) =>
  apiFetch<VersionDetail>(`/versions/${versionId}`);
export const deleteVersion = (versionId: string) =>
  apiFetch<void>(`/versions/${versionId}`, { method: 'DELETE' });

export const uploadProof = (issueId: string, file: File, name?: string) =>
  apiUpload<ProofDetail>(`/issues/${issueId}/proofs`, file, name ? { name } : undefined);
export const uploadVersion = (proofId: string, file: File) =>
  apiUpload<ProofDetail>(`/proofs/${proofId}/versions`, file);

// ── Comment threads ──────────────────────────────────────────────────────────

export const listMembers = (versionId: string) =>
  apiFetch<WorkspaceMember[]>(`/versions/${versionId}/members`);
export const listThreads = (versionId: string) =>
  apiFetch<CommentThread[]>(`/versions/${versionId}/threads`);
export const createThread = (pageId: string, input: CreateThreadInput) =>
  apiFetch<CommentThread>(`/pages/${pageId}/threads`, { method: 'POST', body: input });
export const addComment = (threadId: string, input: CreateCommentInput) =>
  apiFetch<Comment>(`/threads/${threadId}/comments`, { method: 'POST', body: input });
export const resolveThread = (threadId: string) =>
  apiFetch<CommentThread>(`/threads/${threadId}/resolve`, { method: 'POST' });
export const reopenThread = (threadId: string) =>
  apiFetch<CommentThread>(`/threads/${threadId}/reopen`, { method: 'POST' });
export const deleteThread = (threadId: string) =>
  apiFetch<void>(`/threads/${threadId}`, { method: 'DELETE' });

// ── External review links (Business plan) ────────────────────────────────────

export const listShareLinks = (proofId: string) =>
  apiFetch<ShareLink[]>(`/proofs/${proofId}/share-links`);
export const createShareLink = (proofId: string, input: CreateShareLinkInput) =>
  apiFetch<ShareLink>(`/proofs/${proofId}/share-links`, { method: 'POST', body: input });
export const updateShareLink = (linkId: string, input: UpdateShareLinkInput) =>
  apiFetch<ShareLink>(`/share-links/${linkId}`, { method: 'PATCH', body: input });
export const revokeShareLink = (linkId: string) =>
  apiFetch<void>(`/share-links/${linkId}`, { method: 'DELETE' });

// ── The public (guest) side of a review link ─────────────────────────────────

/**
 * Every guest call is scoped by the link token in the path, so they're bundled
 * behind one factory rather than threading `token` through a dozen signatures.
 * The token is a credential: it stays in the URL path and is never logged.
 */
export function shareApi(token: string) {
  const base = `/public/share/${encodeURIComponent(token)}`;
  return {
    portal: () => apiFetch<SharePortal>(base),
    identify: (name: string) =>
      apiFetch<{ id: string; name: string }>(`${base}/identify`, { method: 'POST', body: { name } }),
    version: (versionId: string) => apiFetch<VersionDetail>(`${base}/versions/${versionId}`),
    threads: (versionId: string) =>
      apiFetch<CommentThread[]>(`${base}/versions/${versionId}/threads`),
    createThread: (pageId: string, input: CreateThreadInput) =>
      apiFetch<CommentThread>(`${base}/pages/${pageId}/threads`, { method: 'POST', body: input }),
    addComment: (threadId: string, input: CreateCommentInput) =>
      apiFetch<Comment>(`${base}/threads/${threadId}/comments`, { method: 'POST', body: input }),
    deleteThread: (threadId: string) =>
      apiFetch<void>(`${base}/threads/${threadId}`, { method: 'DELETE' }),
    /** Live-update stream URL, for an EventSource. */
    eventsUrl: (versionId: string) => `/api${base}/versions/${versionId}/events`,
    /** Direct download URL (only works when the link allows downloads). */
    originalUrl: (versionId: string) => `/api${base}/versions/${versionId}/original`,
  };
}

// ── Review gates ─────────────────────────────────────────────────────────────

export const getReview = (versionId: string) =>
  apiFetch<VersionReview>(`/versions/${versionId}/review`);
export const submitDecision = (versionId: string, stepId: string, input: SubmitDecisionInput) =>
  apiFetch<VersionReview>(`/versions/${versionId}/steps/${stepId}/decision`, {
    method: 'POST',
    body: input,
  });
export const withdrawDecision = (versionId: string, stepId: string) =>
  apiFetch<VersionReview>(`/versions/${versionId}/steps/${stepId}/decision`, { method: 'DELETE' });
export const getAudit = (proofId: string) => apiFetch<AuditEvent[]>(`/proofs/${proofId}/audit`);
export const getBoard = (issueId: string) => apiFetch<BoardRow[]>(`/issues/${issueId}/board`);
export const getProjectBoard = (projectId: string) =>
  apiFetch<ProjectBoardGroup[]>(`/projects/${projectId}/board`);

// ── Platform admin ───────────────────────────────────────────────────────────
export const getAdminOverview = () => apiFetch<AdminOverview>('/admin/overview');
export const getAdminAccounts = () => apiFetch<AdminAccount[]>('/admin/accounts');
export const setAdminAccountPlan = (userId: string, plan: WorkspacePlan) =>
  apiFetch<AdminAccount>(`/admin/accounts/${userId}/plan`, { method: 'PATCH', body: { plan } });
export const deleteAdminAccount = (userId: string) =>
  apiFetch<void>(`/admin/accounts/${userId}`, { method: 'DELETE' });
export const getAdminIntegrations = () => apiFetch<AdminIntegrations>('/admin/integrations');
export const updateAdminEmail = (input: UpdateEmailSettingsInput) =>
  apiFetch<AdminIntegrations>('/admin/integrations/email', { method: 'PATCH', body: input });
export const updateAdminRegistration = (open: boolean) =>
  apiFetch<AdminIntegrations>('/admin/integrations/registration', { method: 'PATCH', body: { open } });
export const testAdminStorage = () =>
  apiFetch<StorageTestResult>('/admin/integrations/storage/test', { method: 'POST' });
